import {
  DateArg,
  formatDistance,
  formatDistanceStrict,
  formatDistanceToNowStrict,
  isAfter,
} from 'date-fns';
import { match, P } from 'ts-pattern';
import { Resolution } from './archiveHelpers';
import { Gwei } from './gwei';
import { multiply } from './math';

export function formatDollar(
  value: undefined | bigint,
  options?: Intl.NumberFormatOptions
): string {
  if (value === undefined) return '–';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  }).format(Number(Gwei.fmt(value)));
}
export function formatNumber(
  value: undefined | bigint,
  options?: Intl.NumberFormatOptions
): string {
  if (value === undefined) return '–';
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  }).format(Number(Gwei.fmt(value)));
}
export function formatNumberWithDecimals(value: undefined | bigint): string {
  if (value === undefined) return '–';

  const decimalPlaces = Gwei.fmt(value).split('.')[1]?.length || 0;

  return formatNumber(value, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

export function formatPercentWithDecimals(value: undefined | bigint) {
  if (value === undefined) return '–';

  const percent = multiply(value, Gwei.toGwei('100'));
  const decimalPlaces = Gwei.fmt(percent).split('.')[1]?.length || 0;
  return (
    formatNumber(percent, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }) + '%'
  );
}

export function formatPercentage(
  value: bigint | undefined,
  options?: Intl.NumberFormatOptions
): string {
  if (value === undefined) return '–';

  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  }).format(Number(Gwei.fmt(value)));
}

// yyyy-mm-dd hh:mm:ss
export const getDateTimeFormatter = (options?: Intl.DateTimeFormatOptions) =>
  Intl.DateTimeFormat('sv-SE', {
    dateStyle: 'short',
    timeStyle: 'medium',
    ...options,
  });

export function formatDate(
  value: number | Date | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (value === undefined) return '–';
  return getDateTimeFormatter(options).format(value);
}
/**
 * Returns appropriate DateTimeFormat options based on the specified resolution.
 * @param resolution The time resolution (minute, hour, day, week, month)
 * @returns DateTimeFormat options for the specified resolution
 */
export const getResolutionDateTimeFormatter = (resolution: Resolution) => {
  return match(resolution)
    .returnType<Intl.DateTimeFormatOptions>()
    .with(P.string.startsWith('minute'), () => ({
      timeStyle: 'short',
      dateStyle: undefined,
      hourCycle: 'h23',
    }))
    .with(P.string.startsWith('hour'), () => ({
      dateStyle: undefined,
      timeStyle: 'short',
      hourCycle: 'h23',
    }))
    .with(
      P.union(
        P.string.startsWith('day'),
        P.string.startsWith('week'),
        P.string.startsWith('month')
      ),
      () => ({
        dateStyle: 'short',
        timeStyle: undefined,
      })
    )
    .exhaustive();
};

/**
 * Formats a date based on the specified resolution.
 * @param value Date value to format
 * @param resolution Time resolution determining format (minute, hour, day, week, month)
 * @returns Formatted date string or '–' if value is undefined/null
 */
export function formatDateFromResolution(
  value: number | Date | undefined,
  resolution: Resolution
): string {
  if (value === undefined || value === null) {
    return '–';
  }

  return formatDate(value, getResolutionDateTimeFormatter(resolution));
}

export function formatLockoutDuration(milliseconds: number) {
  const days = Math.floor(milliseconds / 86400000);
  if (days) return `${days}d`;
  const hours = Math.floor(milliseconds / 3600000);
  if (hours) return `${hours}h`;
  const minutes = Math.floor(milliseconds / 60000);
  if (minutes) return `${minutes}m`;
  return `${Math.floor(milliseconds / 1000)}s`;
}

export function formatDuration(value: DateArg<Date> | undefined): string {
  if (value === undefined) return '–';
  return formatDistanceStrict(value, 0);
}

export function formatDateRelative(value: DateArg<Date> | undefined): string {
  if (value === undefined) return '–';
  return formatDistanceToNowStrict(value, {
    addSuffix: true,
  });
}

export function formatExpirationDuration(expiry: DateArg<Date> | undefined): string {
  if (expiry === undefined) return '–';
  const expiryDate = new Date(expiry);
  const now = new Date();
  if (isAfter(now, expiryDate)) {
    return 'Expired';
  }
  return formatDistance(expiryDate, now);
}

type FormatConfig = {
  threshold: bigint;
  units: Array<{
    divisor: bigint;
    suffix: string;
    prefix?: string;
  }>;
  decimals: number;
  smallNumFormatter: (num: bigint) => string;
};
const largeUnits = [
  { divisor: Gwei.toGwei('1000000000000000'), suffix: 'P' }, // For 10^15 (Quadrillion)
  { divisor: Gwei.toGwei('1000000000000'), suffix: 'T' }, // For 10^12 (Trillion)
  { divisor: Gwei.toGwei('1000000000'), suffix: 'B' }, // For 10^9  (Billion)
  { divisor: Gwei.toGwei('1000000'), suffix: 'M' }, // For 10^6  (Million)
  { divisor: Gwei.toGwei('1000'), suffix: 'k' }, // For 10^3  (Thousand)
];
const defaultLargeNumberConfig: FormatConfig = {
  threshold: Gwei.toGwei('10000'),
  units: largeUnits,
  decimals: 2,
  smallNumFormatter: formatNumber,
};

export const formatLargeNumber = (value: bigint, config: Partial<FormatConfig> = {}) => {
  // Merge provided config with defaults, handling bigint values separately
  const finalConfig = {
    ...defaultLargeNumberConfig,
    threshold: config.threshold ?? defaultLargeNumberConfig.threshold,
    units: config.units ?? defaultLargeNumberConfig.units,
    decimals: config.decimals ?? defaultLargeNumberConfig.decimals,
    smallNumFormatter: config.smallNumFormatter ?? defaultLargeNumberConfig.smallNumFormatter,
  };

  const absValue = Gwei.abs(value);

  // If below threshold, use small number formatter
  if (absValue < finalConfig.threshold) {
    return finalConfig.smallNumFormatter(value);
  }

  // Find the appropriate unit for the value
  const unit = finalConfig.units.find((unit) => absValue >= unit.divisor);
  if (!unit) {
    return finalConfig.smallNumFormatter(value);
  }

  // Calculate the formatted value using Gwei division
  const scaledValue = Gwei.div(value, unit.divisor);
  const formattedValue = Number(Gwei.fmt(scaledValue)).toFixed(finalConfig.decimals);

  return `${unit.prefix || ''}${formattedValue}${unit.suffix}`;
};

const dollarConfig: FormatConfig = {
  threshold: Gwei.toGwei('10000'),
  units: largeUnits.map((unit) => ({
    ...unit,
    prefix: `$`,
  })),
  decimals: 2,
  smallNumFormatter: formatDollar,
};

export const formatLargeDollar = (value: bigint) => formatLargeNumber(value, dollarConfig);

// Compact format: "2y 3m", "5d 7h", "1h 30m"
export function formatCompactDuration(value: number | undefined): string {
  if (value === undefined) return '–';

  // For zero duration
  if (value === 0) return '0s';

  // Convert milliseconds to different time units
  const seconds = Math.floor((value / 1000) % 60);
  const minutes = Math.floor((value / (1000 * 60)) % 60);
  const hours = Math.floor((value / (1000 * 60 * 60)) % 24);
  const days = Math.floor((value / (1000 * 60 * 60 * 24)) % 30); // Approximate month as 30 days
  const months = Math.floor((value / (1000 * 60 * 60 * 24 * 30)) % 12); // Approximate
  const years = Math.floor(value / (1000 * 60 * 60 * 24 * 365)); // Approximate

  // Format according to largest non-zero unit
  // Show at most 2 units, e.g., "2y 3m" or "5d 7h" or "1h 30m"
  let result = '';

  if (years > 0) {
    result = `${years}y`;
    if (months > 0) result += ` ${months}m`;
  } else if (months > 0) {
    result = `${months}mo`;
    if (days > 0) result += ` ${days}d`;
  } else if (days > 0) {
    result = `${days}d`;
    if (hours > 0) result += ` ${hours}h`;
  } else if (hours > 0) {
    result = `${hours}h`;
    if (minutes > 0) result += ` ${minutes}m`;
  } else if (minutes > 0) {
    result = `${minutes}m`;
    if (seconds > 0) result += ` ${seconds}s`;
  } else {
    result = `${seconds}s`;
  }

  return result;
}

export const formatWrappedToken = (token: string | undefined) => {
  if (!token) return token;
  const wrappedTokens = ['WETH', 'WBTC', 'WUSDe'];
  if (wrappedTokens.includes(token)) {
    return token.slice(1);
  }
  return token;
};
